
















































import {
  NotesDryingRating,
  NotesDryingRatingAttribute,
  NotesDryingRatingAttributeNames,
} from "@/data/models/NotesDryingRating";
import {
  DryingLabel,
  localizeDryingLabel,
} from "@/data/models/ProgramChainQualityRating";
import { COLOR_NOT_SELECTED_QUALITY_RATING, COLOR_SELECTED_QUALITY_RATING } from "@/utils/colors";
import { themedRatingCell } from "@/utils/Theme";
import { Vue, Component, Model, Prop } from "vue-property-decorator";

@Component
export default class NotesComponent extends Vue {
  @Prop({ default: true })
  editEnabled!: boolean;

  @Model("notesDryingRating")
  notesDryingRating!: NotesDryingRating;
  
  notesDryingRatingAttributeNames = NotesDryingRatingAttributeNames;

  groupedLabels = this.calculateGroupedLabels();

  ratingOptionsForAnomalies = ["relayMalfunction", "newMoistureAppeared", "powerConsumptionTooLow"];
  ratingOptionsGeneral = ["underfloorHeating", "interruptionByCustomers"];
  ratingOptionsForRoomConditions = ["roomHumidityTooHigh", "gkgRoomTooHigh", "gkgConstant", "stableAfterXDays"];
  ratingOptionsForRoomTemperature = ["tooLow", "tooHigh"];
  ratingOptionsForEDTGeneral = ["noPlasticSheetingOnRawFloor", "insufficientAirFlow", "moistureNearDrillHoles", "dryingWithPreDriedAir"];
  ratingOptionsForEDTPhase4 = ["humidityOfProcessAirTooHigh", "gkgOfProcessAirTooHigh"];
  ratingOptionsForEDTPauses = ["gkgDecreasedInPause", "gkgConstantInPause", "gkgIncreasedInPause", "gkgIncreasedAfterPause"];

 get headerRow() {
    return this.$t("rateDrying.qualityNotes.title").toString();
  }

  getRatingFromColumnIndex(columnIndex: number, ratingOptionsLength: number) {
    //Ignore label column
    const adjustedIndex = columnIndex - 1;
    const ratingOffset = Math.floor(ratingOptionsLength / 2);

    const isNumberOfRatingOptionsEven = (ratingOptionsLength % 2) === 0

    if (isNumberOfRatingOptionsEven) {
      if (adjustedIndex - ratingOffset >= 0) {
        // Ignore neutral rating
        return adjustedIndex - ratingOffset + 1;
      }
    }

    return adjustedIndex - ratingOffset;
  }

  getRatingOptionsLength(ratingAttribute: NotesDryingRatingAttribute) {
    if (ratingAttribute === "anomalies") {
      return this.ratingOptionsForAnomalies.length;
    }
    else if (ratingAttribute === "general") {
      return this.ratingOptionsGeneral.length;
    }
    else if (ratingAttribute === "roomConditions") {
      return this.ratingOptionsForRoomConditions.length;
    }
    else if (ratingAttribute === "roomTemperature") {
      return this.ratingOptionsForRoomTemperature.length;
    }
    else if (ratingAttribute === "EDTGeneral") {
      return this.ratingOptionsForEDTGeneral.length;
    }
    else if (ratingAttribute === "EDTPauses") {
      return this.ratingOptionsForEDTPauses.length;
    }
    else {
      return this.ratingOptionsForEDTPhase4.length;
    }
  }

  /** The cell's fill and label class for the active theme (see `themedRatingCell`). */
  ratingCell(lightColor: string) {
    return themedRatingCell(lightColor);
  }

  getRatingColor(
    columnIndex: number,
    ratingAttribute: NotesDryingRatingAttribute
  ) {
    const rating = columnIndex - 1;
    const matchesCurrentRating = this.notesDryingRating[ratingAttribute].includes(rating);

    if (matchesCurrentRating) {
      return COLOR_SELECTED_QUALITY_RATING;
    } else {
      return COLOR_NOT_SELECTED_QUALITY_RATING;
    }
  }

  getNotesDryingRatingOptions(ratingAttribute: NotesDryingRatingAttribute) {
    if (ratingAttribute === "anomalies") {
      return [
        this.$t(`rateDrying.qualityNotes.${ratingAttribute}`),
        ...this.ratingOptionsForAnomalies.map((res) =>
          this.$t(`rateDrying.qualityNotes.${res}`)
        ),
      ];
    }
    else if (ratingAttribute === "general") {
      return [
        this.$t(`rateDrying.qualityNotes.${ratingAttribute}`),
        ...this.ratingOptionsGeneral.map((res) =>
          this.$t(`rateDrying.qualityNotes.${res}`)
        ),
      ];
    }
    else if (ratingAttribute === "roomConditions") {
      return [
        this.$t(`rateDrying.qualityNotes.${ratingAttribute}`),
        ...this.ratingOptionsForRoomConditions.map((res) =>
          this.$t(`rateDrying.qualityNotes.${res}`)
        ),
      ];
    }
    else if (ratingAttribute === "roomTemperature") {
      return [
        this.$t(`rateDrying.qualityNotes.${ratingAttribute}`),
        ...this.ratingOptionsForRoomTemperature.map((res) =>
          this.$t(`rateDrying.qualityNotes.${res}`)
        ),
      ];
    }
    else if (ratingAttribute === "EDTGeneral") {
      return [
        this.$t(`rateDrying.qualityNotes.${ratingAttribute}`),
        ...this.ratingOptionsForEDTGeneral.map((res) =>
          this.$t(`rateDrying.qualityNotes.${res}`)
        ),
      ];
    }
    else if (ratingAttribute === "EDTPauses") {
      return [
        this.$t(`rateDrying.qualityNotes.${ratingAttribute}`),
        ...this.ratingOptionsForEDTPauses.map((res) =>
          this.$t(`rateDrying.qualityNotes.${res}`)
        ),
      ];
    }
    else {
      return [
        this.$t(`rateDrying.qualityNotes.${ratingAttribute}`),
        ...this.ratingOptionsForEDTPhase4.map((res) =>
          this.$t(`rateDrying.qualityNotes.${res}`)
        ),
      ];
    }
  }

  getNotesDryingRatingRow(ratingAttribute: NotesDryingRatingAttribute) {
    return this.getNotesDryingRatingOptions(ratingAttribute);
  }

  toggleNotesQualityRating(
    columnIndex: number,
    ratingAttribute: NotesDryingRatingAttribute
  ) {
    const canSelectMultiple = ratingAttribute !== "roomTemperature";

    const currentRating = this.notesDryingRating[ratingAttribute];
    const newRating = columnIndex - 1;

    const newRatings = currentRating;

    if (currentRating && newRatings) {
      if (canSelectMultiple) {
        if (currentRating.includes(newRating)) {
          const index = newRatings.indexOf(newRating, 0);
          if (index > -1) {
            newRatings.splice(index, 1);
          }
        }
        else {
          newRatings.push(newRating);
        }
      }
      else {
        if (!currentRating.includes(newRating)) {
          newRatings.pop();
          newRatings.push(newRating);
        }
        else {
          newRatings.pop();
        }
      }
    }

    const update = {
      ...this.notesDryingRating,
      [ratingAttribute]: newRatings
    };

    this.$emit("notesDryingRating", update);
  }

  canCellBeClicked(index: number) {
    return index > 0;
  }

  calculateGroupedLabels() {
    const labels = [
      DryingLabel.NO_PLASTIC_SHEETING_ON_RAW_FLOOR,
      DryingLabel.UNDERFLOOR_HEATING,

      DryingLabel.NEW_MOISTURE_APPEARED,
      DryingLabel.NIGHTLY_INTERRUPTION_BY_CLIENT,

      DryingLabel.INSUFFICIENT_AIR_FLOW,
      DryingLabel.RELAY_MALFUNCTION,

      DryingLabel.MOISTURE_NEAR_DRILL_HOLES,
      DryingLabel.DRYING_WITH_PRE_DRIED_AIR,
    ];

    const groupSize = 2;
    const result: DryingLabel[][] = [];

    for (let i = 0; i < labels.length; i += groupSize) {
      result.push(labels.slice(i, i + groupSize));
    }

    return result;
  }

  localizeLabel(dryingLabel: DryingLabel) {
    return localizeDryingLabel(this, dryingLabel);
  }
}
